"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useMemo } from "react"
import { Area, AreaChart, Tooltip } from "recharts"
import { DollarSign, ShoppingCart } from "lucide-react"
import { KpiCard } from "@/components/ui/analytics/kpi-card"
import type { DeltaResult } from "@/lib/analytics/deltas"
import type { DailyDataPoint } from "./use-comparison-data"
import { formatCurrency } from "./utils"
import { useTranslations } from "next-intl"


interface RevenueOrdersKpisProps {
  revenue: number
  orders: number
  revenueDelta?: DeltaResult | null
  ordersDelta?: DeltaResult | null
  dailySeries: DailyDataPoint[]
  ordersHref: string
}

type SparklinePoint = { value: number; label: string }

/** Total Revenue and Total Orders cards, shown on every plan with or without Insights. */
export function RevenueOrdersKpis({
  revenue,
  orders,
  revenueDelta,
  ordersDelta,
  dailySeries,
  ordersHref,
}: RevenueOrdersKpisProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardRevenueOrdersKpis")

  const sparkline = useMemo(() => buildSparklineData(dailySeries), [dailySeries])

  return (
    <>
      <KpiCard
        label={tUi("totalRevenue")}
        icon={DollarSign}
        formattedValue={formatCurrency(revenue)}
        delta={revenueDelta}
        sparkline={renderSparkline(sparkline.revenue, "sparkRevenue", "hsl(var(--chart-1))", formatCurrency)}
      />
      <KpiCard
        label={tUi("totalOrders")}
        icon={ShoppingCart}
        formattedValue={
          <a
            href={ordersHref}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted cursor-pointer hover:text-primary"
          >
            {orders}
          </a>
        }
        delta={ordersDelta}
        sparkline={renderSparkline(sparkline.orders, "sparkOrders", "hsl(var(--chart-2))", (value) => value.toLocaleString(uiFormattingLocale))}
      />
    </>
  )
}

/** Daily points up to a month, weekly sums beyond that. */
function buildSparklineData(dailySeries: DailyDataPoint[]): { revenue: SparklinePoint[]; orders: SparklinePoint[] } {
  if (dailySeries.length <= 31) {
    return {
      revenue: dailySeries.map((d) => ({ value: d.revenue, label: d.date })),
      orders: dailySeries.map((d) => ({ value: d.orders, label: d.date })),
    }
  }

  const weeks: { revenue: number; orders: number; label: string }[] = []
  for (let i = 0; i < dailySeries.length; i += 7) {
    const chunk = dailySeries.slice(i, i + 7)
    weeks.push({
      revenue: chunk.reduce((s, d) => s + d.revenue, 0),
      orders: chunk.reduce((s, d) => s + d.orders, 0),
      label: `${chunk[0].date} – ${chunk[chunk.length - 1].date}`,
    })
  }
  return {
    revenue: weeks.map((w) => ({ value: w.revenue, label: w.label })),
    orders: weeks.map((w) => ({ value: w.orders, label: w.label })),
  }
}

function renderSparkline(points: SparklinePoint[], gradientId: string, color: string, formatValue: (value: number) => string) {
  if (points.length <= 1) return undefined

  return (
    <AreaChart width={120} height={40} data={points}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Tooltip
        position={{ y: 44 }}
        content={({ active, payload }) => {
          if (!active || !payload?.length) return null
          const point = payload[0].payload as SparklinePoint
          return (
            <div className="rounded border bg-background px-2 py-1 text-xs shadow-sm">
              <div className="text-muted-foreground">{point.label}</div>
              <div className="font-medium">{formatValue(point.value)}</div>
            </div>
          )
        }}
      />
      <Area type="monotone" dataKey="value" stroke={color} fill={`url(#${gradientId})`} strokeWidth={1.5} dot={false} isAnimationActive={false} />
    </AreaChart>
  )
}
